import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Award, Flame, Zap, Trophy, Star, CheckCircle2, Database, Fingerprint, Calculator,
  Filter, Terminal, TrendingUp, Briefcase, MapPin, Sparkles, Radar as RadarIcon,
  Activity, GitCommit, ShieldAlert, UserPlus, Check, Clock,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ScoreGaugesRow from "@/components/portfolio/ScoreGaugesRow";
import VerifiedBadgesRow from "@/components/portfolio/VerifiedBadgesRow";
import ShareProfileBar from "@/components/portfolio/ShareProfileBar";
import SkillRadarChart from "@/components/portfolio/SkillRadarChart";
import ActivityHeatmap from "@/components/portfolio/ActivityHeatmap";
import AchievementTimeline from "@/components/portfolio/AchievementTimeline";
import CaseStudyShowcaseSection from "@/components/portfolio/CaseStudyShowcaseSection";
import PerformanceStatsSection from "@/components/portfolio/PerformanceStatsSection";
import CertificateCard from "@/components/portfolio/CertificateCard";
import AICareerInsightsCard from "@/components/portfolio/AICareerInsightsCard";

const ICON_MAP = {
  footprints: Award, flame: Flame, target: CheckCircle2, "trending-up": TrendingUp, zap: Zap,
  award: Award, trophy: Trophy, medal: Trophy, database: Database, fingerprint: Fingerprint,
  calculator: Calculator, filter: Filter, terminal: Terminal, briefcase: Briefcase,
};

export default function PublicProfilePage() {
  const { userId } = useParams();
  const { user: viewer, loading: authLoading } = useAuth();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [friendStatus, setFriendStatus] = useState(null);
  const trackedRef = useRef(null);

  useEffect(() => {
    api.get(`/public/profile/${userId}`)
      .then((res) => setProfile(res.data))
      .catch((e) => setError(e.response?.status === 403 ? "private" : "not_found"))
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => {
    if (authLoading || trackedRef.current === userId) return;
    if (viewer && viewer.user_id === userId) return;
    trackedRef.current = userId;
    const params = new URLSearchParams(window.location.search);
    const source = params.get("src") || "direct";
    api.post(`/public/profile/${userId}/track-view`, { source, referrer: document.referrer }).catch(() => {});
  }, [userId, viewer, authLoading]);

  const trackProjectView = (caseStudyId) => {
    if (viewer && viewer.user_id === userId) return;
    api.post(`/public/profile/${userId}/track-project-view`, { case_study_id: caseStudyId }).catch(() => {});
  };

  useEffect(() => {
    if (viewer && viewer.user_id !== userId) {
      api.get(`/friends/status/${userId}`).then((res) => setFriendStatus(res.data.status)).catch(() => {});
    }
  }, [viewer, userId]);

  const sendFriendRequest = async () => {
    try {
      await api.post(`/friends/request/${userId}`);
      setFriendStatus("pending_sent");
      toast.success("Friend request sent!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send friend request");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="public-profile-loading">
        <div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error === "private") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4 px-6" data-testid="public-profile-private">
        <ShieldAlert className="w-10 h-10 text-slate-400" />
        <p className="text-slate-300">This profile has been set to private by the owner.</p>
        <Link to="/" className="text-indigo-400 hover:text-indigo-300 text-sm">Cloudivo →</Link>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4 px-6" data-testid="public-profile-not-found">
        <p className="text-slate-300">This profile could not be found.</p>
        <Link to="/" className="text-indigo-400 hover:text-indigo-300 text-sm">Cloudivo →</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background px-4 sm:px-6 py-12" data-testid="public-profile-page">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Hero header */}
        <div className="glass-panel rounded-2xl p-8 text-center" data-testid="public-profile-hero">
          <div className="w-24 h-24 rounded-full bg-primary/20 flex items-center justify-center text-3xl font-heading font-bold text-white overflow-hidden border-2 border-indigo-400/30 mx-auto mb-4">
            {profile.picture ? <img src={profile.picture} alt={profile.name} loading="lazy" className="w-full h-full object-cover" /> : profile.name?.[0]}
          </div>
          <h1 className="font-heading text-2xl font-semibold text-white" data-testid="public-profile-name">{profile.name}</h1>
          {profile.headline && <p className="text-sm text-indigo-300 font-medium mt-1" data-testid="public-profile-headline">{profile.headline}</p>}
          {profile.country && (
            <p className="text-xs text-slate-400 mt-1 flex items-center justify-center gap-1">
              <MapPin className="w-3 h-3" /> {profile.country}
            </p>
          )}
          <span className="inline-flex items-center gap-1 mt-3 bg-purple-500/10 border border-purple-500/30 rounded-full px-3 py-1 text-xs font-bold text-purple-300">
            <Star className="w-3 h-3" /> Level {profile.level_info.level} · {profile.level_info.title}
          </span>
          {profile.bio && <p className="text-sm text-slate-400 mt-4 max-w-md mx-auto">{profile.bio}</p>}

          <div className="flex justify-center gap-8 mt-6">
            <div className="text-center"><div className="flex items-center gap-1 text-[#FBBF24] justify-center"><Zap className="w-4 h-4" /><span className="font-heading text-xl font-bold">{profile.xp}</span></div><p className="text-[10px] text-slate-400">XP</p></div>
            <div className="text-center"><div className="flex items-center gap-1 text-indigo-400 justify-center"><Flame className="w-4 h-4" /><span className="font-heading text-xl font-bold">{profile.streak_longest}</span></div><p className="text-[10px] text-slate-400">Best Streak</p></div>
            <div className="text-center"><div className="flex items-center gap-1 text-indigo-400 justify-center"><Trophy className="w-4 h-4" /><span className="font-heading text-xl font-bold">#{profile.rank}</span></div><p className="text-[10px] text-slate-400">Rank</p></div>
          </div>

          <div className="mt-6">
            <VerifiedBadgesRow badges={profile.verified_badges} openToWork={profile.open_to_work} isPremium={profile.is_premium} />
          </div>

          {viewer && viewer.user_id !== userId && (
            <div className="mt-5">
              {friendStatus === "none" && (
                <button onClick={sendFriendRequest} data-testid="public-profile-add-friend-btn" className="inline-flex items-center gap-1.5 text-xs font-semibold bg-indigo-500/15 hover:bg-indigo-500/25 text-indigo-300 px-4 py-2 rounded-full transition-colors">
                  <UserPlus className="w-3.5 h-3.5" /> Add Friend
                </button>
              )}
              {friendStatus === "pending_sent" && (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white/5 text-slate-400 px-4 py-2 rounded-full" data-testid="public-profile-friend-pending">
                  <Clock className="w-3.5 h-3.5" /> Request Sent
                </span>
              )}
              {friendStatus === "pending_received" && (
                <Link to="/friends" data-testid="public-profile-friend-respond-link" className="inline-flex items-center gap-1.5 text-xs font-semibold bg-amber-500/15 text-amber-300 px-4 py-2 rounded-full hover:bg-amber-500/25">
                  <Clock className="w-3.5 h-3.5" /> Respond to Request →
                </Link>
              )}
              {friendStatus === "friends" && (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-emerald-500/15 text-emerald-400 px-4 py-2 rounded-full" data-testid="public-profile-friends-badge">
                  <Check className="w-3.5 h-3.5" /> Friends
                </span>
              )}
            </div>
          )}
        </div>

        {/* AI Score gauges */}
        <div className="glass-panel rounded-2xl p-6" data-testid="public-profile-scores-panel">
          <ScoreGaugesRow scores={profile.scores} />
        </div>

        <ShareProfileBar name={profile.name} />

        {/* Tabbed sections */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="public-profile-tabs" className="flex-wrap h-auto justify-center gap-1 bg-white/[0.03] border border-white/5">
            <TabsTrigger value="overview" data-testid="profile-tab-overview"><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Overview</TabsTrigger>
            <TabsTrigger value="skills" data-testid="profile-tab-skills"><RadarIcon className="w-3.5 h-3.5 mr-1.5" /> Skills &amp; Activity</TabsTrigger>
            <TabsTrigger value="portfolio" data-testid="profile-tab-portfolio"><Briefcase className="w-3.5 h-3.5 mr-1.5" /> Portfolio</TabsTrigger>
            <TabsTrigger value="performance" data-testid="profile-tab-performance"><Activity className="w-3.5 h-3.5 mr-1.5" /> Performance</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6 space-y-6">
            <div>
              <h2 className="font-heading font-semibold text-white mb-3 text-sm flex items-center gap-2"><Sparkles className="w-4 h-4 text-indigo-400" /> AI Career Insights</h2>
              <AICareerInsightsCard insights={profile.career_insights} />
            </div>
            <div className="glass-panel rounded-2xl p-6">
              <h2 className="font-heading font-semibold text-white mb-4 text-sm flex items-center gap-2"><GitCommit className="w-4 h-4 text-emerald-400" /> Achievement Timeline</h2>
              <AchievementTimeline timeline={profile.achievement_timeline} />
            </div>
          </TabsContent>

          <TabsContent value="skills" className="mt-6 space-y-6">
            <div className="glass-panel rounded-2xl p-6" data-testid="public-profile-skill-radar-panel">
              <h2 className="font-heading font-semibold text-white mb-2 text-sm flex items-center gap-2"><RadarIcon className="w-4 h-4 text-indigo-400" /> AI Skill Radar</h2>
              <SkillRadarChart skills={profile.skill_radar} />
            </div>
            <div className="glass-panel rounded-2xl p-6" data-testid="public-profile-heatmap-panel">
              <h2 className="font-heading font-semibold text-white mb-3 text-sm flex items-center gap-2"><Activity className="w-4 h-4 text-indigo-400" /> Learning Activity</h2>
              <ActivityHeatmap data={profile.activity_heatmap} />
            </div>
          </TabsContent>

          <TabsContent value="portfolio" className="mt-6 space-y-6">
            <div className="glass-panel rounded-2xl p-6" data-testid="public-profile-case-studies-panel">
              <h2 className="font-heading font-semibold text-white mb-4 text-sm flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-indigo-400" /> Enterprise Case Studies ({profile.case_study_showcase?.length || 0})
              </h2>
              <CaseStudyShowcaseSection caseStudies={profile.case_study_showcase} onExpand={trackProjectView} />
            </div>

            <div className="glass-panel rounded-2xl p-6">
              <h2 className="font-heading font-semibold text-white mb-4 text-sm">Achievements ({profile.achievements.length})</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {profile.achievements.map((a) => {
                  const Icon = ICON_MAP[a.icon] || Award;
                  return (
                    <div key={a.id} className="text-center" data-testid={`public-achievement-${a.id}`}>
                      <Icon className="w-6 h-6 text-[#FBBF24] mx-auto mb-1.5" />
                      <p className="text-[10px] text-slate-400">{a.title}</p>
                    </div>
                  );
                })}
                {profile.achievements.length === 0 && <p className="text-xs text-slate-400 col-span-4 text-center py-4">No achievements earned yet.</p>}
              </div>
            </div>

            <div className="glass-panel rounded-2xl p-6" data-testid="public-profile-certificate-panel">
              <h2 className="font-heading font-semibold text-white mb-4 text-sm flex items-center gap-2"><Award className="w-4 h-4 text-[#FBBF24]" /> Certificate</h2>
              <CertificateCard certificate={profile.certificate} />
            </div>
          </TabsContent>

          <TabsContent value="performance" className="mt-6">
            <div className="glass-panel rounded-2xl p-6" data-testid="public-profile-performance-panel">
              <PerformanceStatsSection simulatorPerformance={profile.simulator_performance} interviewAnalytics={profile.interview_analytics} />
            </div>
          </TabsContent>
        </Tabs>

        <p className="text-center text-xs text-slate-400 mt-8">
          <Link to="/" className="text-indigo-400 hover:text-indigo-300">Cloudivo</Link> — {profile.completed_count}/30 challenges completed
        </p>
      </div>
    </div>
  );
}
